import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { DepthLabel } from "@/components/Shell";
import { SearchEmpty } from "@/components/states";
import { countryName, countryNames } from "@/lib/countries";
import { useNavigate } from "react-router-dom";
import { recordRecent } from "@/lib/recents";
import { toast } from "sonner";
import { X, Search, Plus, ArrowRight } from "lucide-react";

// M1 — the same palette now also jumps to destinations (user + admin), not only catalogue search.
const NAV_TARGETS = [
  ["Workspace", "/workspace"], ["Billing", "/billing"], ["Marketplace", "/marketplace"],
  ["Hosting", "/hosting"], ["Hire", "/hiring"], ["Features", "/features"],
];
const ADMIN_TARGETS = [
  ["Admin console", "/admin"], ["Command Center", "/admin/command-center"],
  ["Catalogue & Data Governance (Menu 11)", "/admin/governance"],
];

const KINDS = ["connector", "blueprint", "coach", "layer"];
// PATCH 27 Part 3: taxonomy-driven jurisdiction facet. Codes are aliased to real catalogue jur
// codes (GB->UK, PAN->AFRICA) before querying, so every facet is a real, clickable filter.
const JUR_ALIAS = { GB: "UK", PAN: "AFRICA" };
function flattenJurGroups(tax) {
  const groups = [];
  (tax || []).forEach((node) => {
    if (node.bloc) groups.push({ label: node.name, options: (node.members || []).map((mm) => ({ code: mm.code, name: countryName(mm.name || mm.code), zero: mm.connectors === 0 })) });
    else if (node.major_countries) groups.push({ label: "Major countries", options: node.major_countries.map((c) => ({ code: c.code, name: countryName(c.name || c.code) })) });
    else if (node.region) (node.subregions || []).forEach((sr) => groups.push({ label: `${node.name} — ${sr.name}`, options: (sr.members || []).map((code) => ({ code, name: countryName(code) })) }));
    else if (node.global) groups.push({ label: node.name, options: [{ code: node.global, name: node.name }] });
  });
  return groups;
}
const SECTORS = [
  ["FIN", "Finance, Banking & Tax"], ["SCL", "Supply Chain & Logistics"], ["ESG", "ESG & Carbon"],
  ["LEG", "LegalTech & Risk"], ["AUT", "Automotive & Fleet"], ["HRE", "HR, Payroll & Education"],
  ["ECM", "E-Commerce & Retail"], ["HLT", "Healthcare & Public Sector"], ["PRP", "Real Estate & PropTech"],
  ["PLT", "Platform, AI Gov & Cyber"]];
const ACCESS_CLASSES = ["A", "B", "C", "D", "M"];
const STATUSES = ["live", "sandbox", "placeholder", "module", "unavailable"];
const DEPTHS = ["DEEP", "STANDARD", "BASELINE"];

export default function RegistryDrawer({ open, onClose, projectId, onAdded, admin }) {
  const nav = useNavigate();
  const [q, setQ] = useState(""); const [kind, setKind] = useState("connector");
  const [f, setF] = useState({ jurisdiction: "", sector: "", access_class: "", status: "", depth: "" });
  const [res, setRes] = useState(null); const [loading, setLoading] = useState(false);
  const [jurGroups, setJurGroups] = useState([]);

  useEffect(() => {
    api.get("/public/region-taxonomy").then(r => setJurGroups(flattenJurGroups(r.data.taxonomy))).catch(() => {});
  }, []);

  const jurName = (code) => {
    for (const g of jurGroups) for (const o of g.options) if ((JUR_ALIAS[o.code] || o.code) === code) return o.name;
    return code;
  };

  const run = async () => {
    setLoading(true);
    try {
      const params = { q, kind, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
      const { data } = await api.get("/catalogue/search", { params });
      setRes(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) run(); /* eslint-disable-next-line */ }, [open, kind, f]);
  if (!open) return null;

  const items = [...(res?.catalogue || []), ...(res?.registry || [])];
  const add = async (it) => {
    if (!projectId) { toast.error("Open a project to add catalogue items"); return; }
    try { const { data } = await api.post(`/projects/${projectId}/add-catalogue`, { entry_id: it.id });
      recordRecent({ type: "connector", id: it.id, label: it.name || it.title || it.id, to: `/workspace/project/${projectId}` });
      toast.success(`Added ${it.name}`); onAdded?.(data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const sel = (key, opts, labelFn) => (
    <select className="k-control" data-testid={`facet-${key}`} style={{ padding: "6px 8px", fontSize: 12 }}
      value={f[key]} onChange={e => setF({ ...f, [key]: e.target.value })}>
      <option value="">{key.replace("_", " ")}</option>
      {opts.map(o => Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{labelFn ? labelFn(o) : o}</option>)}
    </select>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(17,17,19,.6)", zIndex: 60 }} onClick={onClose}>
      <div className="k-container k-fadein" data-testid="registry-drawer" onClick={e => e.stopPropagation()}
        style={{ position: "absolute", right: 0, top: 0, height: "100vh", width: 520, borderRadius: 0, padding: 24, overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 className="k-heading" style={{ fontSize: 20, margin: 0 }}>Search & Discovery</h2>
          <button className="k-btn k-btn-ghost" data-testid="registry-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ display: "flex", gap: 8, margin: "16px 0" }}>
          <input className="k-control" data-testid="registry-query" placeholder="Search catalogues, or type to filter destinations…" value={q}
            onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && run()} />
          <button className="k-btn k-btn-primary" data-testid="registry-search" onClick={run}><Search size={15} /></button>
        </div>
        {/* M1 — Go-to destinations (Admin mode included for platform admins) */}
        <div data-testid="palette-goto" style={{ marginBottom: 14 }}>
          <div className="k-muted" style={{ fontSize: 11, marginBottom: 4 }}>Go to</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {[...NAV_TARGETS, ...(admin ? ADMIN_TARGETS : [])]
              .filter(([label]) => !q || label.toLowerCase().includes(q.toLowerCase()))
              .map(([label, to]) => (
                <button key={to} className="k-badge" data-testid={`goto-${to}`} style={{ cursor: "pointer", fontSize: 12 }}
                  onClick={() => { recordRecent({ type: "screen", id: to, label, to }); onClose?.(); nav(to); }}>{label} <ArrowRight size={11} /></button>
              ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
          {KINDS.map(k => (
            <button key={k} className={`k-badge ${kind === k ? "k-badge-accent" : ""}`} style={{ cursor: "pointer" }}
              data-testid={`registry-kind-${k}`} onClick={() => setKind(k)}>{k}</button>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 14 }}>
          <select className="k-control" data-testid="facet-jurisdiction" style={{ padding: "6px 8px", fontSize: 12 }}
            value={f.jurisdiction} onChange={e => setF({ ...f, jurisdiction: e.target.value })}>
            <option value="">jurisdiction</option>
            {jurGroups.map(g => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map(o => (
                  <option key={o.code + g.label} value={JUR_ALIAS[o.code] || o.code}>
                    {o.name}{o.zero ? " (none yet)" : ""}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {sel("sector", SECTORS)}
          {sel("access_class", ACCESS_CLASSES, o => `class ${o}`)}
          {sel("status", STATUSES)}
          {sel("depth", DEPTHS)}
        </div>
        {loading ? <p className="k-muted">Loading…</p> : (
          <>
            <p className="k-muted" style={{ fontSize: 13 }}>{res?.count ?? 0} results</p>
            {(res?.count ?? 0) === 0 && q && !f.jurisdiction && (
              <SearchEmpty query={q} hint="Try fewer words, a different spelling, another catalogue tab, or clear a filter." testid="registry-search-empty" />
            )}
            {(res?.count ?? 0) === 0 && f.jurisdiction && (
              <p className="k-tag-warn" data-testid="jur-zero-result" style={{ fontSize: 12, padding: 8, borderRadius: 8 }}>
                No connectors catalogued for {jurName(f.jurisdiction)} yet — EU-wide and global connectors
                plus UK/EU compliance layers still apply to your build.
              </p>
            )}
            {items.map((it, i) => {
              const addable = (it.kind === "connector" || it.kind === "blueprint") && it.access_class !== "D";
              return (
                <div key={i} className="k-control" style={{ marginBottom: 8 }} data-testid={`result-${it.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <span className="k-heading" style={{ fontSize: 14, fontWeight: 600 }}>{it.name || it.title}</span>
                    <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      {it.depth && <DepthLabel depth={it.depth} />}
                      {addable && <button className="k-link" data-testid={`add-${it.id}`} onClick={() => add(it)}><Plus size={14} /> Add</button>}
                    </span>
                  </div>
                  <div className="k-muted" style={{ fontSize: 12, marginTop: 4 }}>
                    {it.id}{it.access_class ? ` · class ${it.access_class}` : ""}{it.status ? ` · ${it.status}` : ""}{it.jurisdiction?.length ? ` · ${countryNames(it.jurisdiction)}` : ""}
                  </div>
                  {it.access_class === "D" && <div className="k-tag-red" style={{ fontSize: 12, marginTop: 4 }}>Class D — cannot be added. {it.note}</div>}
                  {it.status === "placeholder" && <div className="k-muted" style={{ fontSize: 12, marginTop: 2 }}>Placeholder: you must obtain your own access.</div>}
                  {it.kind === "connector" && !it.access_route_verified && it.access_class !== "D" &&
                    <div className="k-tag-warn" style={{ fontSize: 12, marginTop: 4 }}>Access route unverified</div>}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
